/**
 * کشیدنِ سنکی. چیدمان کارِ `@/core/lib/sankey` است و این فایل فقط SVG می‌سازد.
 *
 * جدا نگه داشتنشان عمدی است: چیدمان ریاضی است و با `npm test` آزموده می‌شود
 * (sankey.test.ts، با اعدادِ واقعیِ ابر)؛ این‌جا فقط شکل است و آزمودنش ارزشِ
 * چندانی ندارد.
 *
 * ═══ ⚠ دو عدد، و کارتِ شناور ═══
 *
 * تا امروز نمودار تنها جای این صفحه بود که یک عدد داشت و نه دو تا، و برچسبش
 * فقط یک اسم بود. هر دو حالا درست شده‌اند:
 *
 *   • کنارِ هر گره **دو** عدد می‌آید — ده روزِ اخیر، و کم‌رنگ‌تر آخرین اجرا —
 *     همان قاعده‌ای که `ui/Num.tsx` برای کلِ صفحه گذاشته.
 *   • بردنِ موس روی هر گره کارتی باز می‌کند با توضیحِ آن پله و یک ردیفِ
 *     **واقعی** از دیتابیس. عددِ «۱۱٬۲۳۹ کشِ لینکِ مرده» می‌گوید چیزی خراب
 *     است ولی نمی‌گوید چه چیزی؛ یک آدرسِ واقعی می‌گوید.
 *
 * ⚠ عددِ دوم فقط **نوشته** می‌شود و شکل نمی‌دهد: ضخامتِ جریان و ارتفاعِ گره
 * همچنان از ستونِ ده روز می‌آیند. اگر به آخرین اجرا بند می‌شد، اجرایی که فقط
 * یک فاز دویده نموداری تقریباً خالی می‌ساخت.
 *
 * ⚠ کارت با مختصاتِ **موس** جا می‌گیرد و نه با مختصاتِ SVG: نمودار با
 * `viewBox` مقیاس می‌خورد، پس مختصاتِ داخلی‌اش با پیکسلِ صفحه یکی نیست و
 * تبدیلشان یک ضریبِ اضافه می‌خواست که هر بار تغییرِ عرض خرابش می‌کرد.
 */
import React, { useRef, useState } from 'react';
import { layoutSankey, type SankeyLink, type SankeyNode } from '@/core/lib/sankey';

const fmt = (n: number) => n.toLocaleString('en-US');

interface Hover { id: string; label: string; why?: string; value: number; run?: number;
  x: number; y: number; flip: boolean }

export function SankeyChart({ nodes, links, height = 430, nodePadding, example }: {
  nodes: SankeyNode[];
  links: SankeyLink[];
  height?: number;
  /**
   * ⚠ بیرون داده شده چون ستونی با هشت گره بدونش برچسب روی برچسب می‌اندازد:
   * فاصله‌ی گره‌ها از **مقدارشان** می‌آید، پس گرهِ کوچک تقریباً صفر ارتفاع
   * دارد و دو سطرِ برچسبش می‌رود روی گرهِ بعدی. بالا بردنِ `height` تنهایی
   * درستش نمی‌کند — گره‌ها به همان نسبت بزرگ می‌شوند.
   */
  nodePadding?: number;
  /** ردیفِ واقعیِ زیرِ توضیح در کارتِ شناور. شناسه‌ی گره را می‌گیرد. */
  example?: (nodeId: string) => React.ReactNode;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  // پهنا ثابت است و SVG با viewBox کش می‌آید — یعنی روی هر عرضی درست
  // مقیاس می‌شود بدونِ اینکه لازم باشد اندازه‌ی واقعیِ عنصر را اندازه بگیریم.
  const W = 900;
  // جا برای برچسبِ سمتِ راست، وگرنه اسمِ ستونِ آخر بیرونِ کادر می‌افتد.
  const PAD_R = 96;
  // ⚠ بالا و پایین قرینه نیستند، و نباید باشند: برچسبِ هر گره **زیرِ** لبه‌ی
  // بالایی‌اش شروع می‌شود و سطرِ دومش ۱۷ پیکسل پایین‌تر است. گرهِ آخرِ یک
  // ستون که مقدارش کوچک باشد (پنج پیکسل ارتفاع) عددش دوازده پیکسل از خودش
  // می‌زند پایین‌تر و با فضای قرینه بریده می‌شد.
  const PAD_T = 13;
  const PAD_B = 30;
  // ⚠ کلید فقط وقتی گذاشته می‌شود که مقدار دارد. `layoutSankey` گزینه‌ها را
  // با spread روی پیش‌فرض‌ها می‌ریزد، و `{nodePadding: undefined}` پیش‌فرض را
  // **پاک** می‌کند نه اینکه ردش کند — یعنی هر نمودارِ دیگری بدونِ فاصله‌ی
  // گره‌ها کشیده می‌شد.
  const l = layoutSankey(nodes, links, {
    width: W - PAD_R, height, ...(nodePadding ? { nodePadding } : {}),
  });

  const terminal = new Set(l.nodes.filter((n) => n.terminal).map((n) => n.id));

  const track = (n: typeof l.nodes[number]) => (e: React.MouseEvent) => {
    const box = wrap.current?.getBoundingClientRect();
    if (!box) return;
    const x = e.clientX - box.left;
    setHover({
      id: n.id, label: n.label, why: n.why, value: n.value, run: n.run,
      x, y: e.clientY - box.top,
      // نزدیکِ لبه‌ی راست، کارت به چپِ اشاره‌گر می‌رود تا از کادر نزند بیرون.
      flip: x > box.width * 0.55,
    });
  };

  return (
    <div className="sankeywrap" ref={wrap} onMouseLeave={() => setHover(null)}>
      <svg className="sankey" viewBox={`0 0 ${W} ${height + PAD_T + PAD_B}`} role="img"
        aria-label="جریانِ چرخه‌ی عمرِ کرالر">
        <g transform={`translate(0,${PAD_T})`}>
          {/* جریان‌ها اول کشیده می‌شوند تا گره‌ها رویشان بنشینند. */}
          {l.links.map((k) => (
            <path key={`${k.from}-${k.to}`}
              className={`flow${terminal.has(k.to) ? ' drop' : ''}`}
              d={k.path} strokeWidth={k.thickness}>
              <title>
                {`${k.from} → ${k.to}${k.label ? ` (${k.label})` : ''}: ${fmt(k.value)}`}
              </title>
            </path>
          ))}

          {l.nodes.map((n) => (
            // ⚠ ناحیه‌ی حساسِ موس یک مستطیلِ **نامرئی** روی گره و برچسبش است
            // و نه خودِ گره: گرهِ کوچک چهار پیکسل ارتفاع دارد و گرفتنش با موس
            // تقریباً ناممکن بود، در حالی که برچسبش کنارش کاملاً دیده می‌شود.
            //
            // ⚠ و عمداً تا تهِ نمودار کشیده نمی‌شود: مستطیلِ تمام‌عرضِ گرهِ
            // ریشه روی همه‌چیز می‌افتاد و هر جای خالیِ نمودار کارتِ «درخواستِ
            // صفحه» را باز می‌کرد. ۱۶۰ یعنی «گره + بلندترین برچسب».
            <g key={n.id} onMouseMove={track(n)}
              className={hover?.id === n.id ? 'nodeg on' : 'nodeg'}>
              <rect className="hit" x={n.x} y={n.y - 4}
                width={n.width + 160} height={Math.max(n.height, 26) + 8} />
              <rect className={`node${n.terminal ? ' terminal' : ''}`}
                x={n.x} y={n.y} width={n.width} height={n.height} rx={2} />
              <text x={n.x + n.width + 6} y={n.y + Math.min(n.height / 2, 10) + 4}>
                {n.label}
              </text>
              <text className="v" x={n.x + n.width + 6}
                y={n.y + Math.min(n.height / 2, 10) + 17}>
                {fmt(n.value)}
                {n.run === undefined ? null : (
                  <tspan className="r">{`  ·  ${fmt(n.run)}`}</tspan>
                )}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {hover ? (
        <div className="sankeytip"
          style={hover.flip
            ? { right: `calc(100% - ${hover.x - 14}px)`, top: hover.y + 12 }
            : { left: hover.x + 14, top: hover.y + 12 }}>
          <div className="tiphead">{hover.label}</div>
          <div className="tipnums">
            <span><b>{fmt(hover.value)}</b> در ۱۰ روز</span>
            {hover.run === undefined
              ? null
              : <span className="dim"><b>{fmt(hover.run)}</b> در آخرین اجرا</span>}
          </div>
          {hover.why ? <p>{hover.why}</p> : null}
          {example ? example(hover.id) : null}
        </div>
      ) : null}
    </div>
  );
}
